// The one state vocabulary, and the only place a plate shape is chosen.
//
// Ladeway has three independent axes, not one (refresh_plan.md section 1):
// custody is the noun, telemetry is the adverb, sync is the verb. A row can be
// delivered_confirmed AND untracked AND local_only at the same time, which is
// why the axes get three visually unrelated plate shapes (SIGNALWORKS, chosen
// in ai_docs/design/progress_log.md) and can never collapse back into one green
// pill. Seven divergent chip vocabularies across the SPA (audit_web.md) collapse
// into this file.
//
// What lives here:
//   - the public value names the plan enumerates (`delivered_unconfirmed`, not
//     the token's shortened `delivered-unconf`),
//   - the token slug each value reads its colours from, so a component writes
//     `var(--lw-${slug}-fg)` and never a literal,
//   - the plate shape the token file says that value is ALLOWED to render as,
//   - the label text, so "Confirmed" is spelled the same on the board, on the
//     load detail and in the POD panel.
//
// Colours are NOT here. They are custom properties, resolved by the browser at
// paint time, so a dark theme is an attribute swap and not a JS branch.
//
// `plate` and `glyph` are asserted against design-tokens/tokens.json by
// __tests__/state.test.js. If someone changes a plate shape in the token source
// and not here, that test fails: this file may restate the token decision, it
// may not disagree with it.
//
// ONE FILE, and it was not always. While the live map was a separate Vue 3
// package that deliberately imported NOTHING from this tree, it held a
// byte-identical copy of this vocabulary, gated by a parity spec — the same
// mechanism the token file uses for the same reason. The live map is a route of
// this app now (ui/src/app/pages/Tracking), its rail imports LwChip from beside
// this file, and the copy and its parity spec are gone. A dispatcher crossing
// from the board to the live map reads the same word for the same fact because
// there is only one place the word is written.

/**
 * The eight plate shapes, from the token file's PLATE SHAPES banner.
 *
 *   guide         square plate with an inset white rule; states a fact about
 *                 the route. Custody only.
 *   guide-quiet   the same square at low chroma; the fact is settled or dormant.
 *   guide-dashed  dashed edge; stated, not yet countersigned.
 *   guide-struck  label struck through; withdrawn.
 *   warning       amber plate, chamfered top-right corner; a human should look.
 *                 Telemetry staleness owns this and nothing else may borrow it.
 *   regulatory    white disc, red ring; something required is missing.
 *   neutral       flat tinted rectangle, no signage meaning.
 *   none          no plate at all; the quietest state in an axis is plain text.
 */
export const PLATE_SHAPES = [
  'guide',
  'guide-quiet',
  'guide-dashed',
  'guide-struck',
  'warning',
  'regulatory',
  'neutral',
  'none',
];

export const AXES = ['custody', 'telemetry', 'sync', 'feedback'];

/**
 * axis -> value -> { token, plate, label, glyph? }
 *
 * `token` is the custom-property stem: `--lw-${token}-fg`, `-bg`, `-edge`.
 */
export const STATE_VOCABULARY = Object.freeze({
  // Where is the freight in its chain of responsibility? The only axis allowed
  // the full colour range.
  custody: Object.freeze({
    awaiting_pickup: { token: 'custody-awaiting', plate: 'guide-quiet', label: 'Awaiting pickup' },
    in_transit: { token: 'custody-in-transit', plate: 'guide', label: 'In transit' },
    // The one custody value tinted amber, because it is the one custody value
    // that owes a named human an action. The token file's judgement call comes
    // with a rule this library has to keep: never render a telemetry warning
    // plate on the same row as this custody plate, or the two ambers read as
    // one alarm. The dashed edge is what keeps them apart when they do meet.
    delivered_unconfirmed: {
      token: 'custody-delivered-unconf',
      plate: 'guide-dashed',
      label: 'Driver completed',
    },
    // THE one green in the system, and deliberately the calmest row on the
    // board rather than the loudest.
    delivered_confirmed: {
      token: 'custody-delivered-conf',
      plate: 'guide-quiet',
      label: 'Confirmed',
    },
    unknown: { token: 'custody-unknown', plate: 'guide-quiet', label: 'State unknown' },
    cancelled: { token: 'custody-cancelled', plate: 'guide-struck', label: 'Cancelled' },
  }),

  // How well do we know that? Non-hue channels only, and an age string is
  // always rendered next to it by the caller. `live` has no plate on purpose:
  // there is nothing for a human to do about a working tracker, and a green
  // pill for liveness is exactly how staleness gets faked.
  telemetry: Object.freeze({
    live: { token: 'tel-live', plate: 'none', label: 'Live' },
    stale: { token: 'tel-stale', plate: 'warning', label: 'Silent' },
    untracked: { token: 'tel-untracked', plate: 'regulatory', label: 'No device' },
    unknown: { token: 'tel-unknown', plate: 'neutral', label: 'Not asked' },
  }),

  // Has the office got it yet? A glyph plus a word, never a bare colour: the
  // words are the plan's (refresh_plan.md 1.4) and they are customer-facing
  // sentences rather than status names, because a driver reads them.
  sync: Object.freeze({
    pending: { token: 'sync-pending', plate: 'neutral', label: 'Syncing', glyph: 'arrow-up' },
    local_only: {
      token: 'sync-local-only',
      plate: 'regulatory',
      label: 'Saved on this phone',
      glyph: 'phone-slash',
    },
    // The quietest of the four, by rule: a system that paints success loudly
    // has nothing left to say when something is wrong.
    synced: { token: 'sync-synced', plate: 'none', label: 'Synced', glyph: 'check' },
    synced_partial: {
      token: 'sync-partial',
      plate: 'warning',
      label: 'Synced, a photo did not upload',
      glyph: 'arrow-up-half',
    },
  }),

  // Generic UI feedback, not a freight fact. A saved form is not a delivered
  // load, and only the second one gets to be about freight.
  feedback: Object.freeze({
    success: { token: 'success', plate: 'guide-quiet', label: 'Success' },
    warning: { token: 'warning', plate: 'warning', label: 'Warning' },
    danger: { token: 'danger', plate: 'regulatory', label: 'Error' },
    info: { token: 'info', plate: 'neutral', label: 'Info' },
    neutral: { token: 'neutral', plate: 'neutral', label: 'Neutral' },
  }),
});

/** Every value name on an axis, for prop validators and for the demo page. */
export function valuesFor(axis) {
  return Object.keys(STATE_VOCABULARY[axis] || {});
}

/**
 * The entry for one axis/value pair, or null when the pair does not exist.
 * Callers validate rather than fall back: an unknown state is a bug in the
 * caller, and rendering a plausible-looking plate for it would hide that.
 */
export function stateEntry(axis, value) {
  const forAxis = STATE_VOCABULARY[axis];
  if (!forAxis) return null;
  return forAxis[value] || null;
}

/** `--lw-custody-in-transit-fg` and friends, for one axis/value pair. */
export function stateVar(axis, value, role) {
  const entry = stateEntry(axis, value);
  return entry ? `var(--lw-${entry.token}-${role})` : null;
}
